import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AvatarLook } from "@/lib/office/avatar-look";
import type { AwardKind } from "@/lib/office/awards";
import { vyllageProfileKeys } from "@/lib/query";

export type VyllageProfile = {
  id: string;
  userId: string;
  displayName: string;
  avatarColors: AvatarLook;
  points: number;
  streak: number;
  lastCheckInDay: string | null;
  unlockedIds: string[];
  /** ISO timestamp when the user first saved a look; null until then. */
  lookSavedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

async function parseError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") return err;
    }
  } catch {
    // ignore
  }
  return res.statusText || "Request failed";
}

function parseProfile(data: unknown): VyllageProfile {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid profile payload");
  }
  const row = data as Record<string, unknown>;
  if (typeof row.displayName !== "string") {
    throw new Error("Invalid profile payload");
  }
  return {
    id: typeof row.id === "string" ? row.id : "",
    userId: typeof row.userId === "string" ? row.userId : "",
    displayName: row.displayName,
    avatarColors: row.avatarColors as AvatarLook,
    points: typeof row.points === "number" ? row.points : 0,
    streak: typeof row.streak === "number" ? row.streak : 0,
    lastCheckInDay: typeof row.lastCheckInDay === "string" ? row.lastCheckInDay : null,
    unlockedIds: Array.isArray(row.unlockedIds)
      ? row.unlockedIds.filter((x): x is string => typeof x === "string")
      : [],
    lookSavedAt:
      typeof row.lookSavedAt === "string"
        ? row.lookSavedAt
        : row.lookSavedAt instanceof Date
          ? row.lookSavedAt.toISOString()
          : null,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
    updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : "",
  };
}

export function useVyllageProfile() {
  return useQuery({
    queryKey: vyllageProfileKeys.me(),
    queryFn: async (): Promise<VyllageProfile> => {
      const res = await fetch("/api/me/vyllage-profile", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) {
        throw new Error("Invalid profile response");
      }
      return parseProfile((body as { data: unknown }).data);
    },
    // Profile is create-on-read; brief 500s (migrate/restart) should retry.
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    staleTime: 15_000,
  });
}

/** Public look update — displayName + avatarColors only. */
export function useUpdateVyllageProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      displayName?: string;
      avatarColors?: VyllageProfile["avatarColors"];
    }) => {
      const res = await fetch("/api/me/vyllage-profile", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) {
        throw new Error("Invalid profile response");
      }
      return parseProfile((body as { data: unknown }).data);
    },
    onSuccess: (data) => {
      // Keep cache hot so /office?ready=1 sees lookSavedAt immediately after flush.
      // Prefer setQueryData over invalidate to avoid remounting dirty dashboard drafts.
      queryClient.setQueryData(vyllageProfileKeys.me(), data);
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to save look");
    },
  });
}

export type ProgressionAwardResponse = {
  data: VyllageProfile;
  awarded: boolean;
  delta: number;
  reason: string;
};

async function parseAwardResponse(res: Response): Promise<ProgressionAwardResponse> {
  if (!res.ok) throw new Error(await parseError(res));
  const body: unknown = await res.json();
  if (!body || typeof body !== "object" || !("data" in body)) {
    throw new Error("Invalid award response");
  }
  const row = body as Record<string, unknown>;
  return {
    data: parseProfile(row.data),
    awarded: row.awarded === true,
    delta: typeof row.delta === "number" ? row.delta : 0,
    reason: typeof row.reason === "string" ? row.reason : "",
  };
}

/** Daily check-in — server awards fixed points once per UTC day. */
export function useVyllageCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/me/vyllage-profile/check-in", {
        method: "POST",
        credentials: "include",
      });
      return parseAwardResponse(res);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(vyllageProfileKeys.me(), result.data);
    },
  });
}

/** Cap-gated social awards (wave / chat / tour / meeting). */
export function useVyllageAward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (kind: AwardKind) => {
      const res = await fetch("/api/me/vyllage-profile/award", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind }),
      });
      return parseAwardResponse(res);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(vyllageProfileKeys.me(), result.data);
    },
  });
}
